import * as React from 'react';
import { Map } from '@vis.gl/react-google-maps';

// Utils
import { formatDate, formatDateRange } from '@src/shared/src/util/date';
import {
  isHotelOnlySearch,
  isRentalOnlySearch,
  isRoundtripSearch,
} from '@src/shared/src/util/search';
// Constants
import { GOOGLE_MAPS_MAP_IDS } from '@toolkit/const/app';
// Actions, Models & Interfaces
import { CoordinatesModel, SearchModel } from '@src/shared/src/models';
// Components
import { MapMarker } from '@pod/search/components';
import SearchMapPolyline from './SearchMapPolyline';
// Styles

type Props = {
  search: SearchModel;
};

const SearchMap: React.FC<Props> = ({ search }) => {
  const [currentZoomValue, setCurrentZoomValue] = React.useState(6);

  const handleZoomChanged = (map: google.maps.Map) => {
    const newZoom = map.getZoom();
    if (newZoom !== undefined) {
      setCurrentZoomValue(newZoom);
    }
  };

  const shouldShowArrivalMarker = (sectionIndex: number) => {
    const isLastSection = sectionIndex === search.sections.length - 1;
    // show arrival marker if it's the last section
    if (isLastSection) return true;

    // show arrival marker if the next section's departure location is different from the current section's arrival location
    if (search.sections[sectionIndex + 1].depName !== search.sections[sectionIndex].arrName)
      return true;

    return false;
  };

  const renderMapMarker = (lat: number, lng: number, name: string, date?: string) => {
    const coord = new CoordinatesModel(lat, lng);
    if (coord.exists) {
      return <MapMarker position={coord} locationLabel={name} dateLabel={date} />;
    }
  };

  const renderMapElement = () => {
    if (isHotelOnlySearch(search)) {
      return renderMapMarker(
        search.arrLat,
        search.arrLng,
        search.arrName,
        search.depAt && search.arrAt && formatDateRange(search.depAt, search.arrAt),
      );
    }

    if (isRentalOnlySearch(search)) {
      return (
        <>
          {renderMapMarker(
            search.depLat,
            search.depLng,
            search.depName,
            search.depAt && formatDate(search.depAt),
          )}
          {renderMapMarker(
            search.arrLat,
            search.arrLng,
            search.arrName,
            search.arrAt && formatDate(search.arrAt),
          )}
          <SearchMapPolyline
            depLat={search.depLat}
            depLng={search.depLng}
            arrLat={search.arrLat}
            arrLng={search.arrLng}
          />
        </>
      );
    }

    return search.sections?.map((section, index) => (
      <span key={`section-${index}-markers`}>
        {renderMapMarker(
          section.depLat,
          section.depLng,
          section.depName,
          section.depAt && formatDate(section.depAt),
        )}
        {isRoundtripSearch(search) && index === 1 ? null : (
          <>
            {shouldShowArrivalMarker(index) &&
              renderMapMarker(section.arrLat, section.arrLng, section.arrName)}
            <SearchMapPolyline
              depLat={section.depLat}
              depLng={section.depLng}
              arrLat={section.arrLat}
              arrLng={section.arrLng}
            />
          </>
        )}
      </span>
    ));
  };

  return (
    <div className="map-container">
      <div className="map-element">
        <Map
          defaultZoom={currentZoomValue}
          defaultCenter={{ lat: 51, lng: 8 }}
          onZoomChanged={(event) => handleZoomChanged(event.map)}
          minZoom={3}
          maxZoom={8}
          fullscreenControl={false}
          mapTypeControl={false}
          streetViewControl={false}
          mapId={GOOGLE_MAPS_MAP_IDS.SEARCH_MAP_ID}
          reuseMaps={true}>
          {renderMapElement()}
        </Map>
      </div>
    </div>
  );
};

export default SearchMap;
